import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getAgencyAgents,
  getAgencyAgentsDropdown,
  getAgencyPlayersBoard,
  getAgencyPartners,
  agencyPromoteAdmin,
  agencyDemoteAdmin,
} from "../../services/api";
import { DETAILED_POSITION_OPTIONS } from "../../components/common/DetailedPositionsControl";

const useAgencyProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Tab states
  const [activeTab, setActiveTab] = useState("agents");
  const [activeAgentType, setActiveAgentType] = useState("licensed");
  const [selectedPosition, setSelectedPosition] = useState("all");
  // Players board controls (design 2a/2b): default sort = contract expiring
  // soonest; contract-status segment + "expiring soon" quick filter; search is
  // client-side so the server counts reflect the filter set, not the query.
  const [playerSort, setPlayerSort] = useState("contract_expiring");
  const [contractFilter, setContractFilter] = useState(""); // ''|expired|expiring|neutral|no_data
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [managingAgentFilter, setManagingAgentFilter] = useState(""); // ''|account_id
  const [nationalityFilter, setNationalityFilter] = useState(""); // ''|country name
  const [playerSearch, setPlayerSearch] = useState("");
  const [playerCounts, setPlayerCounts] = useState(null);
  // Filter option sources: agents from the dropdown endpoint (stable), and
  // nationalities accumulated from the roster so the list doesn't shrink when
  // a nationality filter is applied.
  const [agentOptions, setAgentOptions] = useState([]);
  const [nationalityOptions, setNationalityOptions] = useState([]);

  // Data states
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState(null);

  const [playersData, setPlayersData] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState(null);

  const [partnersData, setPartnersData] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState(null);

  // Modal states
  const [showAgentInviteModal, setShowAgentInviteModal] = useState(false);
  const [showPartnerInviteModal, setShowPartnerInviteModal] = useState(false);
  const [showChangeAgentModal, setShowChangeAgentModal] = useState(false);

  // Popover states
  const [popoverAgent, setPopoverAgent] = useState(null);
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPlayer, setPopoverPlayer] = useState(null);
  const [showPlayerPopover, setShowPlayerPopover] = useState(false);

  // Other states
  const [transferringAgentId, setTransferringAgentId] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  // Admin promote/demote confirmation modal: { agent, action: 'promote'|'demote' } | null
  const [adminActionTarget, setAdminActionTarget] = useState(null);
  const [adminActionLoading, setAdminActionLoading] = useState(false);

  // Fetch agents. We always pull the full roster (type=all) and let the
  // redesigned toolbar do the All/Licensed/Unlicensed segment + search + live
  // counts client-side (design 1a/1c). The `type` arg is accepted for
  // backwards-compat with existing callers but no longer scopes the request.
  const fetchAgents = useCallback(async (type, signal) => {
    setAgentsLoading(true);
    setAgentsError(null);
    try {
      const res = await getAgencyAgents({ type: "all" }, signal);
      const data = res.data?.results?.data || {};
      const licensed = Array.isArray(data.licensed) ? data.licensed : [];
      const unlicensed = Array.isArray(data.unlicensed) ? data.unlicensed : [];
      setAgents([...licensed, ...unlicensed]);
    } catch (e) {
      // A superseded request was aborted (tab switched / unmounted) — not a
      // real error, and the newer request owns the loading state now.
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        return;
      }
      console.error("Failed to fetch agents", e);
      setAgentsError("Failed to load agents");
      setAgents([]);
    } finally {
      // Don't flip loading off for an aborted request — the newer one is still
      // in flight and controls the spinner.
      if (!signal?.aborted) {
        setAgentsLoading(false);
      }
    }
  }, []);

  // Fetch players from the board endpoint (server-side filter/sort/counts).
  const fetchPlayers = useCallback(async () => {
    setPlayersLoading(true);
    setPlayersError(null);
    try {
      const params = { sort: playerSort };
      if (selectedPosition && selectedPosition !== "all") params.position = selectedPosition;
      if (contractFilter) params.contract_status = contractFilter;
      if (expiringSoon) params.expiring_soon = 1;
      if (managingAgentFilter) params.managing_agent = managingAgentFilter;
      if (nationalityFilter) params.nationality = nationalityFilter;
      const res = await getAgencyPlayersBoard(params);
      setPlayersData(Array.isArray(res.data?.results) ? res.data.results : []);
      setPlayerCounts(res.data?.counts || null);
    } catch (e) {
      console.error("Failed to fetch players", e);
      setPlayersError("Failed to load players");
      setPlayersData([]);
    } finally {
      setPlayersLoading(false);
    }
  }, [playerSort, selectedPosition, contractFilter, expiringSoon, managingAgentFilter, nationalityFilter]);

  // Fetch partners
  const fetchPartners = useCallback(async () => {
    setPartnersLoading(true);
    setPartnersError(null);
    try {
      // partner-wall returns a flat {results:[{type,state,...}]} list.
      const res = await getAgencyPartners();
      setPartnersData(res.data?.results || res.data?.data || []);
    } catch (e) {
      console.error("Failed to fetch partners", e);
      setPartnersError("Failed to load partners");
      setPartnersData([]);
    } finally {
      setPartnersLoading(false);
    }
  }, []);

  // Fetch data based on active tab. Abort the in-flight agents request when the
  // tab/sub-tab changes so rapid switching doesn't pile up pending requests
  // (which left "Loading agents…" stuck).
  useEffect(() => {
    const controller = new AbortController();
    if (activeTab === "agents") {
      fetchAgents(activeAgentType, controller.signal);
    } else if (activeTab === "players") {
      fetchPlayers();
    } else if (activeTab === "partners") {
      fetchPartners();
    }
    return () => controller.abort();
  }, [activeTab, activeAgentType, selectedPosition, fetchAgents, fetchPlayers, fetchPartners]);

  // Create positions array
  const positions = useMemo(() => [
    { id: "all", label: "All positions" },
    { id: "goalkeeper", label: "Goalkeeper" },
    { id: "defender", label: "Defender" },
    { id: "midfielder", label: "Midfielder" },
    { id: "forward", label: "Forward" },
    ...DETAILED_POSITION_OPTIONS.map((option) => ({
      id: option.value,
      label: option.value.toUpperCase(),
      fullLabel: option.label,
    })),
    { id: "unknown", label: "Position not set" },
  ], []);

  // Client-side search over the server-sorted board results. Position,
  // contract-status, "expiring soon" and sort are all applied server-side by
  // the board endpoint, so here we only narrow by the free-text query — the
  // server counts stay authoritative for the filter set.
  const visiblePlayers = useMemo(() => {
    const q = playerSearch.trim().toLowerCase();
    if (!q) return playersData;
    return playersData.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [playersData, playerSearch]);

  // Managing-agent filter options — same source as the change-agent picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAgencyAgentsDropdown();
        const list = res.data?.results || res.data || [];
        if (!cancelled) setAgentOptions(Array.isArray(list) ? list : []);
      } catch (_) {
        /* non-fatal: filter just stays empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Accumulate distinct nationalities seen in the roster so the dropdown stays
  // stable even while a nationality filter narrows the visible players.
  useEffect(() => {
    setNationalityOptions((prev) => {
      const seen = new Set(prev.map((n) => n.name));
      const merged = [...prev];
      playersData.forEach((p) => {
        // Every citizenship goes into the dropdown (L-FE-1); the filter itself stays single-valued (BE matches any passport).
        const list = Array.isArray(p.nationalities) && p.nationalities.length ? p.nationalities : [p.nationality || p.country];
        list.forEach((nat) => {
          const name = nat && typeof nat === "object" ? nat.name : nat;
          if (name && !seen.has(name)) {
            seen.add(name);
            merged.push({ name });
          }
        });
      });
      return merged.length === prev.length
        ? prev
        : merged.sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [playersData]);

  // Agent handlers
  const handleAgentMenuClick = useCallback((agent) => {
    setPopoverAgent(agent);
    setShowPopover(true);
  }, []);

  // Read current user id so self-demote can trigger a full reload —
  // when a user removes their own admin status, their JWT still has
  // the old permissions cached in Redux until the next /users/profile/
  // fetch. Simplest robust path is to reload.
  const currentUserId = useSelector(
    (state) => state?.auth?.user?.account_id || state?.auth?.user?.id || null,
  );

  // Promote/demote now open a confirmation modal instead of acting immediately
  // (demote previously used window.confirm). The actual API call runs in
  // confirmAdminAction, which drives the modal's loading spinner.
  const handleMakeAdmin = useCallback((agent) => {
    if (!agent) return;
    setAdminActionTarget({ agent, action: "promote" });
  }, []);

  const handleRemoveAdmin = useCallback((agent) => {
    if (!agent) return;
    setAdminActionTarget({ agent, action: "demote" });
  }, []);

  const cancelAdminAction = useCallback(() => {
    if (adminActionLoading) return; // don't allow cancel mid-request
    setAdminActionTarget(null);
  }, [adminActionLoading]);

  const confirmAdminAction = useCallback(async () => {
    if (!adminActionTarget?.agent) return;
    const { agent, action } = adminActionTarget;
    const isSelf =
      currentUserId && Number(agent.account_id) === Number(currentUserId);
    try {
      setAdminActionLoading(true);
      setTransferringAgentId(agent.account_id);
      const response =
        action === "promote"
          ? await agencyPromoteAdmin(agent.account_id)
          : await agencyDemoteAdmin(agent.account_id);
      if (!response.success) {
        toast.error(
          response.error ||
            (action === "promote"
              ? "Failed to promote user"
              : "Failed to demote user"),
        );
        return;
      }
      toast.success(
        response.message ||
          (action === "promote"
            ? "User promoted to agency admin"
            : "User demoted from agency admin"),
      );
      setAdminActionTarget(null);
      if (action === "demote" && isSelf) {
        // Self-demote: force a full reload so Redux + navigation re-pick up
        // the lost AgencyAdmin permissions.
        setTimeout(() => {
          window.location.assign("/");
        }, 1200);
      } else {
        fetchAgents(activeAgentType);
      }
    } catch (e) {
      toast.error(
        action === "promote"
          ? "Failed to promote user"
          : "Failed to demote user",
      );
    } finally {
      setAdminActionLoading(false);
      setTransferringAgentId(null);
    }
  }, [adminActionTarget, currentUserId, fetchAgents, activeAgentType]);

  // Player handlers
  const handlePlayerMenuClick = useCallback((player) => {
    setPopoverPlayer(player);
    setShowPlayerPopover(true);
  }, []);

  const handleChangeAgent = useCallback((player) => {
    setSelectedPlayer(player);
    setShowChangeAgentModal(true);
  }, []);

  const handleChangeAgentSuccess = useCallback(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  // Navigation handlers
  const handlePlayerClick = useCallback((playerId) => {
    navigate(`/search/player/${playerId}`);
  }, [navigate]);

  const handleAgentClick = useCallback((accountId) => {
    navigate(`/search/agent/${accountId}`);
  }, [navigate]);

  const handlePartnerClick = useCallback((partnerId) => {
    navigate(`/search/agency/${partnerId}`);
  }, [navigate]);

  return {
    // Tab states
    activeTab,
    setActiveTab,
    activeAgentType,
    setActiveAgentType,
    selectedPosition,
    setSelectedPosition,

    // Data
    agents,
    agentsLoading,
    agentsError,
    playersData: visiblePlayers,
    playersLoading,
    playersError,
    partnersData,
    partnersLoading,
    partnersError,

    // Players board controls + counts
    playerSort,
    setPlayerSort,
    contractFilter,
    setContractFilter,
    expiringSoon,
    setExpiringSoon,
    managingAgentFilter,
    setManagingAgentFilter,
    nationalityFilter,
    setNationalityFilter,
    agentOptions,
    nationalityOptions,
    playerSearch,
    setPlayerSearch,
    playerCounts,

    // Filtered data
    positions,

    // Modal states
    showAgentInviteModal,
    setShowAgentInviteModal,
    showPartnerInviteModal,
    setShowPartnerInviteModal,
    showChangeAgentModal,
    setShowChangeAgentModal,

    // Popover states
    popoverAgent,
    showPopover,
    setShowPopover,
    popoverPlayer,
    showPlayerPopover,
    setShowPlayerPopover,

    // Other states
    transferringAgentId,
    selectedPlayer,

    // Fetch functions
    fetchAgents,
    fetchPlayers,
    fetchPartners,

    // Handlers
    handleAgentMenuClick,
    handleMakeAdmin,
    handleRemoveAdmin,
    // Admin promote/demote confirmation modal
    adminActionTarget,
    adminActionLoading,
    confirmAdminAction,
    cancelAdminAction,
    handlePlayerMenuClick,
    handleChangeAgent,
    handleChangeAgentSuccess,
    handlePlayerClick,
    handleAgentClick,
    handlePartnerClick,
  };
};

export default useAgencyProfile;
